import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";

import { getInvitedUsers } from "@/actions/invite-user";
import { getPeopleByOrgId } from "@/actions/people";
import { queryKeys } from "@/constants/query";
import { isValidOrgId } from "@/lib/utils";
import type { TeamMemberModel } from "@/models/MemberModel";

const getMembersQuery = async (orgId: string) => {
  const people = await getPeopleByOrgId(orgId);
  const members: TeamMemberModel[] = people.map((person) => ({
    ...person,
    name: `${person.firstName} ${person.lastName}`,
    personId: person.id,
    email: person.email,
  }));

  // Get set of member emails to filter out already accepted invitations
  const memberEmails = new Set(
    members.map((member) => member.email?.toLowerCase()).filter(Boolean),
  );

  const invitedUsers = await getInvitedUsers(orgId);

  // Filter out invited users that have already been accepted (have a Person record)
  const pendingInvitations = invitedUsers.filter(
    (invitedUser) =>
      !invitedUser.email || !memberEmails.has(invitedUser.email.toLowerCase()),
  );

  return [...members, ...pendingInvitations];
};

export const usePeople = () => {
  const params = useParams();
  const orgId = params?.orgId as string;

  const {
    data: people = [],
    isLoading,
    refetch,
  } = useQuery<TeamMemberModel[]>({
    queryKey: queryKeys.getMembersByOrgId(orgId),
    queryFn: () => getMembersQuery(orgId),
    enabled: isValidOrgId(orgId),
  });

  return {
    people,
    isLoading,
    refetch,
  };
};

export const getPeopleOptions = (
  people: TeamMemberModel[],
  search: string = "",
) => {
  // Convert to Option format and filter by search term
  const peopleOptions = people
    .filter((person) => {
      if (!search) return true;

      const searchLower = search.toLowerCase();
      const fullName = `${person.firstName || ""} ${person.lastName || ""}`
        .trim()
        .toLowerCase();
      const email = (person.email || "").toLowerCase();

      return fullName.includes(searchLower) || email.includes(searchLower);
    })
    .map((person) => ({
      value: person.id || "", // Use id as the GUID for assignedTo
      label: `${person.firstName || ""} ${person.lastName || ""}`.trim(),
    }));

  // Add "Unassigned" option at the beginning
  const options = [{ label: "Unassigned", value: "" }, ...peopleOptions];

  return options;
};
